import React, { useEffect, useState } from 'react';
import LOGOS_DATA from './logosClientes.json';

// Carrusel de logos de clientes de la home (nov-2026).
//
// La lista (logosClientes.json) y los PNG de public/clientes/ los genera
// scripts/logos-clientes.mjs a partir de los logos oficiales de
// marketing/logos-clientes/originales/. No llamar a la lista «clientLogos.json»:
// en Windows webpack la confunde con este componente al importar «ClientLogos»
// sin extensión (prueba .json antes que .jsx). Condición del permiso de los clientes:
// siempre en blanco y negro y pequeños, así que vienen ya en gris y aquí no
// recuperan el color al pasar el ratón.
//
// Con la lista vacía no se pinta. `embedded`: va dentro de otra sección (en la
// home, al final del bloque de digitalización), sin fondo ni márgenes propios.
//
// Orden (4-nov-2026, Jesús: «que no empiecen siempre por los mismos»): el HTML
// prerenderizado lleva el orden del JSON y, ya en el navegador, se baraja en cada
// visita. Nunca quedan juntos los competidores (p. ej. Sika y Master Builders),
// tampoco al dar la vuelta la cinta (el último va seguido del primero).
//
// Dos líneas (4-nov-2026, a partir de 12 logos): mismo tamaño en las dos (Jesús
// propuso una inferior más pequeña; se descartó porque se leería como un ranking de
// clientes) y en sentidos opuestos. La regla de competidores se aplica dentro de
// cada línea; uno encima de otro no se lee como «juntos».

const CLIENT_LOGOS = LOGOS_DATA.logos || [];
const RIVALS = LOGOS_DATA.competidores || [];

const rivals = (a, b) => RIVALS.some(([x, y]) => (a === x && b === y) || (a === y && b === x));
const validOrder = (list) => list.every((l, i) => !rivals(l.slug, list[(i + 1) % list.length].slug));

const TWO_ROWS_FROM = 12;

const splitRows = (list) => {
  if (list.length < TWO_ROWS_FROM) return [list];
  const half = Math.ceil(list.length / 2);
  return [list.slice(0, half), list.slice(half)];
};

/** Baraja y reparte en líneas, cada una sin competidores seguidos. */
const shuffleRows = (list) => {
  for (let attempt = 0; attempt < 100; attempt++) {
    const c = [...list];
    for (let i = c.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [c[i], c[j]] = [c[j], c[i]];
    }
    const rows = splitRows(c);
    if (rows.every(validOrder)) return rows;
  }
  return splitRows(list);
};

// Cada línea va dos veces seguida: la animación desplaza la pista un 50 % y vuelve
// a empezar sin salto. Cada logo lleva su propio margen derecho (no `gap`) para
// que las dos mitades midan exactamente lo mismo.
const MarqueeRow = ({ logos, reverse }) => {
  const track = [...logos, ...logos];
  return (
    <div className="logo-marquee overflow-hidden">
      <ul className={`logo-marquee-track flex w-max items-center ${reverse ? 'logo-marquee-track--reverse' : ''}`}>
        {track.map((logo, i) => {
          const duplicate = i >= logos.length;
          return (
            <li key={`${logo.name}-${i}`} className="shrink-0 pr-12 md:pr-16" aria-hidden={duplicate || undefined}>
              <img
                src={logo.src}
                alt={duplicate ? '' : logo.name}
                width={logo.width}
                height={logo.height}
                className="block opacity-70"
                loading="lazy"
                decoding="async"
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const ClientLogos = ({ title, embedded = false }) => {
  const [rows, setRows] = useState(() => splitRows(CLIENT_LOGOS));
  useEffect(() => { setRows(shuffleRows(CLIENT_LOGOS)); }, []);
  if (!CLIENT_LOGOS.length) return null;

  const marquee = (
    <>
      <p className="text-center text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-10">
        {title}
      </p>
      <div className="logo-marquee-group space-y-7 md:space-y-8">
        {rows.map((row, i) => <MarqueeRow key={i} logos={row} reverse={i % 2 === 1} />)}
      </div>
    </>
  );

  if (embedded) {
    return (
      <div className="mt-16 md:mt-20 pt-12 border-t border-slate-200" role="region" aria-label={title}>
        {marquee}
      </div>
    );
  }
  return (
    <section className="py-14 bg-white border-b border-slate-200" aria-label={title}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{marquee}</div>
    </section>
  );
};

export default ClientLogos;
